import React, { useState, useMemo } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  useTheme,
  IconButton,
  Tooltip,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  Divider,
  Menu,
  MenuItem,
  InputAdornment,
  Drawer,
  Card,
  CardContent,
  LinearProgress,
  Tabs,
  Tab,
  Avatar,
  Badge,
  ListItemAvatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  Business as BusinessIcon,
  TrendingUp as TrendingUpIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Archive as ArchiveIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Assignment as AssignmentIcon,
  MoreVert as MoreVertIcon,
  Note as NoteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Analytics as AnalyticsIcon,
  Message as MessageIcon,
  Email as EmailIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Send as SendIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Sms as SmsIcon,
  Reply as ReplyIcon,
  Visibility as VisibilityIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIndicatorIcon,
  ArrowForward as ArrowForwardIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { NewHire } from '../../services/hireService';
import { ProgressStage } from '../../services/progressStageService';
import TrademarkFooter from '../common/TrademarkFooter';
import TimeSinceCell from '../common/TimeSinceCell';
import { alpha } from '@mui/material/styles';

// Demo data - Progress Stages
const DEMO_PROGRESS_STAGES: ProgressStage[] = [
  { 
    value: 0, 
    label: 'Invitation Sent', 
    description: 'Initial invitation has been sent to the candidate',
    color: '#ff6b6b'
  },
  { 
    value: 1, 
    label: 'Pending Enrollment', 
    description: 'Candidate is pending enrollment in the course',
    color: '#feca57'
  },
  { 
    value: 2, 
    label: 'Enrolled in Course', 
    description: 'Candidate is currently enrolled in the course',
    color: '#48dbfb'
  },
  { 
    value: 3, 
    label: 'Passed the Test Waiting License Approval', 
    description: 'Candidate passed the test and is waiting for license approval',
    color: '#ff9ff3'
  },
  { 
    value: 4, 
    label: 'Company Onboarding Waiting Agent Number Approval', 
    description: 'Company onboarding in progress, waiting for agent number approval',
    color: '#54a0ff'
  },
  { 
    value: 5, 
    label: 'Company Onboarding Agent Number Approved', 
    description: 'Agent number has been approved, onboarding continues',
    color: '#5f27cd'
  },
  { 
    value: 6, 
    label: 'Agent Number Approved Waiting for Training', 
    description: 'Agent number approved, waiting for training to begin',
    color: '#00d2d3'
  }
];

// Generate fake hire data
const generateFakeHires = (): NewHire[] => {
  const firstNames = ['John', 'Jane', 'Michael', 'Sarah', 'David', 'Emily', 'Robert', 'Jessica', 'William', 'Ashley', 'James', 'Amanda', 'Christopher', 'Melissa', 'Daniel', 'Nicole'];
  const lastNames = ['Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis', 'Rodriguez', 'Martinez', 'Hernandez', 'Lopez', 'Wilson', 'Anderson', 'Thomas', 'Taylor'];
  const states = ['TX', 'CA', 'FL', 'NY', 'IL', 'PA', 'OH', 'GA', 'NC', 'MI'];
  const emails = ['gmail.com', 'yahoo.com', 'outlook.com', 'hotmail.com'];
  const instagramHandles = ['@johnsmith', '@janesmith', '@mikejones', '@sarahbrown', '@davidwilson'];
  const references = ['Friend', 'LinkedIn', 'Referral', 'Website', 'Social Media'];
  const recruiterNames = ['John Recruiter', 'Jane Manager', 'Mike Admin', 'Sarah Owner'];
  const recruiterEmails = ['john.recruiter@example.com', 'jane.manager@example.com', 'mike.admin@example.com', 'sarah.owner@example.com'];
  const recruiterNumbers = ['555-0101', '555-0102', '555-0103', '555-0104'];

  const hires: NewHire[] = [];
  const now = new Date();

  // Generate hires for each progress stage
  DEMO_PROGRESS_STAGES.forEach((stage, stageIndex) => {
    const hiresPerStage = stageIndex === 0 ? 5 : 3; // More for first stage
    
    for (let i = 0; i < hiresPerStage; i++) {
      const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
      const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];
      const daysAgo = Math.floor(Math.random() * 60) + 1;
      const createdDate = new Date(now);
      createdDate.setDate(createdDate.getDate() - daysAgo);
      
      const updatedDate = new Date(createdDate);
      if (stage.value > 0) {
        updatedDate.setDate(updatedDate.getDate() + Math.floor(Math.random() * 10));
      }

      const recruiterIndex = Math.floor(Math.random() * recruiterNames.length);
      const mobileNumber = `555-${Math.floor(Math.random() * 9000) + 1000}`;
      
      hires.push({
        id: `demo-${stage.value}-${i}`,
        created_at: createdDate.toISOString(),
        fullName: `${firstName} ${lastName}`,
        state: states[Math.floor(Math.random() * states.length)],
        email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@${emails[Math.floor(Math.random() * emails.length)]}`,
        mobileNumber: mobileNumber,
        progress: stage.value,
        instagram: instagramHandles[Math.floor(Math.random() * instagramHandles.length)],
        org_id: 1,
        update_timestamp: updatedDate.toISOString(),
        checkListNum: String(stage.value + 1),
        reference: references[Math.floor(Math.random() * references.length)],
        recruiter_numbers: recruiterNumbers[recruiterIndex],
        recruiter_emails: [recruiterEmails[recruiterIndex]],
        agent_appointment_sent: stage.value === 6 ? Math.random() > 0.5 : false,
        notes: `Demo notes for ${firstName} ${lastName}. This is sample data for demonstration purposes.`,
      });
    }
  });

  return hires;
};

// Demo team members
const DEMO_TEAM_MEMBERS = [
  {
    id: 'demo-owner-1',
    full_name: 'Sarah Owner',
    role: 'owner',
    team_member_email: 'sarah.owner@example.com',
    email: 'sarah.owner@example.com',
  },
  {
    id: 'demo-admin-1',
    full_name: 'John Recruiter',
    role: 'admin',
    team_member_email: 'john.recruiter@example.com',
    email: 'john.recruiter@example.com',
  },
  {
    id: 'demo-admin-2',
    full_name: 'Jane Manager',
    role: 'admin',
    team_member_email: 'jane.manager@example.com',
    email: 'jane.manager@example.com',
  },
  {
    id: 'demo-admin-3',
    full_name: 'Mike Admin',
    role: 'admin',
    team_member_email: 'mike.admin@example.com',
    email: 'mike.admin@example.com',
  },
];

// Demo stats
const DEMO_STATS = {
  activeHires: 24,
  totalHires: 35,
  archivedHires: 11,
};

// Demo team member metrics
const DEMO_TEAM_METRICS: Record<string, any> = {
  'demo-owner-1': {
    enrolled: 15,
    waiting: 8,
    ratio: 0.53,
    currentEnrolled: 12,
    currentWaiting: 5,
    currentRatio: 0.42,
    jotformEnrolled: 20,
    jotformInvitations: 35,
    jotformRatio: 0.57,
    jotformCurrentEnrolled: 15,
    jotformCurrentInvitations: 25,
    jotformCurrentRatio: 0.60,
  },
  'demo-admin-1': {
    enrolled: 8,
    waiting: 4,
    ratio: 0.50,
    currentEnrolled: 6,
    currentWaiting: 3,
    currentRatio: 0.50,
    jotformEnrolled: 12,
    jotformInvitations: 20,
    jotformRatio: 0.60,
    jotformCurrentEnrolled: 9,
    jotformCurrentInvitations: 15,
    jotformCurrentRatio: 0.60,
  },
  'demo-admin-2': {
    enrolled: 10,
    waiting: 5,
    ratio: 0.50,
    currentEnrolled: 8,
    currentWaiting: 4,
    currentRatio: 0.50,
    jotformEnrolled: 15,
    jotformInvitations: 25,
    jotformRatio: 0.60,
    jotformCurrentEnrolled: 12,
    jotformCurrentInvitations: 20,
    jotformCurrentRatio: 0.60,
  },
  'demo-admin-3': {
    enrolled: 6,
    waiting: 3,
    ratio: 0.50,
    currentEnrolled: 4,
    currentWaiting: 2,
    currentRatio: 0.50,
    jotformEnrolled: 10,
    jotformInvitations: 18,
    jotformRatio: 0.56,
    jotformCurrentEnrolled: 7,
    jotformCurrentInvitations: 12,
    jotformCurrentRatio: 0.58,
  },
};

// Demo SMS data
const generateDemoSMS = () => {
  const names = ['John Smith', 'Jane Doe', 'Michael Johnson', 'Sarah Williams', 'David Brown'];
  const messages = [
    'Hi! I\'m interested in learning more about the opportunity.',
    'Thanks for reaching out! When can we schedule a call?',
    'I completed the enrollment form. What\'s next?',
    'I have a question about the training schedule.',
    'Great! Looking forward to getting started.',
  ];
  const now = new Date();
  const sms: any[] = [];
  
  for (let i = 0; i < 15; i++) {
    const daysAgo = Math.floor(Math.random() * 30);
    const date = new Date(now);
    date.setDate(date.getDate() - daysAgo);
    const isInbound = Math.random() > 0.4;
    
    sms.push({
      id: `sms-${i}`,
      name: names[Math.floor(Math.random() * names.length)],
      number: `555-${Math.floor(Math.random() * 9000) + 1000}`,
      email: `user${i}@example.com`,
      message: messages[Math.floor(Math.random() * messages.length)],
      cost: isInbound ? 0 : (Math.random() * 0.05).toFixed(4),
      direction: isInbound ? 'inbound' : 'outbound',
      seen: Math.random() > 0.3,
      created_at: date.toISOString(),
    });
  }
  
  return sms.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
};

// Demo Email Reminders
const generateDemoEmails = () => {
  const subjects = [
    'Welcome to Our Team - Next Steps',
    'Reminder: Complete Your Enrollment',
    'Training Schedule Update',
    'License Approval Status',
    'Agent Number Assignment',
  ];
  const now = new Date();
  const emails: any[] = [];
  
  for (let i = 0; i < 12; i++) {
    const daysAgo = Math.floor(Math.random() * 60);
    const date = new Date(now);
    date.setDate(date.getDate() - daysAgo);
    
    emails.push({
      id: `email-${i}`,
      from: 'noreply@example.com',
      to: `hire${i}@example.com`,
      subject: subjects[Math.floor(Math.random() * subjects.length)],
      body: `<p>Dear Team Member,</p><p>This is a demo email reminder about your onboarding process.</p><p>Best regards,<br/>HR Team</p>`,
      sent_at: date.toISOString(),
    });
  }
  
  return emails.sort((a, b) => new Date(b.sent_at).getTime() - new Date(a.sent_at).getTime());
};

// Demo Automated AI Messages
const generateDemoAIMessages = () => {
  return [
    {
      id: 'ai-1',
      trigger: 'Invitation Sent',
      message: 'Hi {{name}}! Welcome to our team. Please complete your enrollment form at {{link}}.',
      enabled: true,
    },
    {
      id: 'ai-2',
      trigger: 'Enrollment Complete',
      message: 'Great job {{name}}! Your enrollment is confirmed. Next step: complete the training course.',
      enabled: true,
    },
    {
      id: 'ai-3',
      trigger: 'Training Reminder',
      message: 'Hi {{name}}, this is a friendly reminder that your training course is available. Start anytime!',
      enabled: false,
    },
    {
      id: 'ai-4',
      trigger: 'License Approved',
      message: 'Congratulations {{name}}! Your license has been approved. We\'ll assign your agent number soon.',
      enabled: true,
    },
  ];
};

// Demo Checklist Content
const generateDemoChecklistContent = () => {
  return [
    {
      title: 'Welcome & Introduction',
      content: [
        { text: 'Welcome to our organization!' },
        { text: 'Review company policies and procedures' },
        { text: 'Complete your profile setup' },
      ],
    },
    {
      title: 'Enrollment Process',
      content: [
        { text: 'Submit enrollment form' },
        { text: 'Provide required documentation' },
        { text: 'Complete background check' },
      ],
    },
    {
      title: 'Training & Certification',
      content: [
        { text: 'Complete online training course' },
        { text: 'Pass certification exam' },
        { text: 'Attend orientation session' },
      ],
    },
  ];
};

const OwnerDashboardDemo: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [hires] = useState<NewHire[]>(generateFakeHires());
  const [filteredHires, setFilteredHires] = useState<NewHire[]>(generateFakeHires());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editFormData, setEditFormData] = useState<Partial<NewHire>>({});
  const [selectedTeamMember, setSelectedTeamMember] = useState<any>(null);
  const [collapsedStages, setCollapsedStages] = useState<Set<number>>(new Set());
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedHireForActions, setSelectedHireForActions] = useState<NewHire | null>(null);
  const [search, setSearch] = useState('');
  const [sidebarMinimized, setSidebarMinimized] = useState(false);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [selectedHireForNotes, setSelectedHireForNotes] = useState<NewHire | null>(null);
  const [smsTab, setSmsTab] = useState(0);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<any>(null);
  const [configTab, setConfigTab] = useState<'content' | 'automation' | 'progressStages' | null>(null);
  const theme = useTheme();

  const progressStages = DEMO_PROGRESS_STAGES;
  const stats = DEMO_STATS;
  const teamMembers = DEMO_TEAM_MEMBERS;
  const teamMemberMetrics = DEMO_TEAM_METRICS;
  const ownerProfile = { full_name: 'Demo Owner', org_id: 1 };
  const [demoSMS] = useState(generateDemoSMS());
  const [demoEmails] = useState(generateDemoEmails());
  const [demoAIMessages] = useState(generateDemoAIMessages());
  const [demoChecklistContent] = useState(generateDemoChecklistContent());

  const adminTeamMembers = teamMembers.filter(member => member.role === 'admin');
  const ownerTeamMembers = teamMembers.filter(member => member.role === 'owner');

  const agentNumberCheckboxStage = useMemo(() => {
    const pendingStage = progressStages.find(stage =>
      stage.label.toLowerCase().includes('pending waiting for training')
    );
    if (pendingStage) return pendingStage.value;

    const waitingApprovalStage = progressStages.find(stage =>
      stage.label.toLowerCase().includes('waiting agent number approval')
    );
    if (waitingApprovalStage) return waitingApprovalStage.value;

    const genericAgentStage = progressStages.find(stage =>
      stage.label.toLowerCase().includes('agent number')
    );
    return genericAgentStage?.value ?? 6; // Default to stage 6 for demo
  }, [progressStages]);

  const tableColumns = [
    'Actions',
    'Notes',
    'Created At',
    'Updated At',
    'Time Since',
    'Full Name',
    'State',
    'Email',
    'Mobile Number',
    'Instagram',
    'Reference',
    'Recruiter Numbers',
    'Recruiter Emails',
    'Edit',
  ];

  const getTableColumnsForStage = (stageValue: number, agentStageValue: number | null) => {
    if (agentStageValue !== null && stageValue === agentStageValue) {
      const baseColumns = [...tableColumns];
      baseColumns.splice(2, 0, 'Agent Number Sent');
      return baseColumns;
    }
    return tableColumns;
  };

  const handleTabChange = (newValue: number) => {
    setSelectedTab(newValue);
    if (newValue !== 0) {
      setConfigTab(null);
    }
  };

  const navigationItems = [
    { id: 0, label: 'Overview', icon: <DashboardIcon /> },
    { id: 1, label: 'Team Members', icon: <PeopleIcon /> },
    { id: 2, label: 'Analytics', icon: <AnalyticsIcon /> },
    { id: 3, label: 'SMS Manager', icon: <MessageIcon /> },
    { id: 4, label: 'Email Reminders', icon: <EmailIcon /> },
  ];

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'primary';
      case 'new_hire':
        return 'secondary';
      case 'owner':
        return 'success';
      default:
        return 'default';
    }
  };

  const handleEdit = (hire: NewHire) => {
    setEditingId(hire.id);
    setEditFormData(hire);
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditFormData({});
  };

  const handleSave = () => {
    // Demo - just close edit mode
    setEditingId(null);
    setEditFormData({});
  };

  const handleEditChange = (field: keyof NewHire) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleTeamMemberClick = (member: any) => {
    setSelectedTeamMember(selectedTeamMember?.id === member.id ? null : member);
    const teamMemberEmail = member.team_member_email || member.email;
    const filtered = hires.filter(hire => {
      if (!hire.recruiter_emails) return false;
      const recruiterEmailsStr = Array.isArray(hire.recruiter_emails) 
        ? hire.recruiter_emails.join(',')
        : String(hire.recruiter_emails);
      return recruiterEmailsStr.toLowerCase().includes(teamMemberEmail.toLowerCase());
    });
    setFilteredHires(filtered);
  };

  const handleOwnerMemberClick = (member: any) => {
    handleTeamMemberClick(member);
  };

  const handleShowAllHires = () => {
    setSelectedTeamMember(null);
    setFilteredHires(hires);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = event.target.value;
    setSearch(searchValue);
    
    let filtered = selectedTeamMember 
      ? hires.filter(hire => {
          const teamMemberEmail = selectedTeamMember.team_member_email || selectedTeamMember.email;
          if (!hire.recruiter_emails) return false;
          const recruiterEmailsStr = Array.isArray(hire.recruiter_emails) 
            ? hire.recruiter_emails.join(',')
            : String(hire.recruiter_emails);
          return recruiterEmailsStr.toLowerCase().includes(teamMemberEmail.toLowerCase());
        })
      : hires;

    if (searchValue) {
      filtered = filtered.filter(hire => {
        const searchLower = searchValue.toLowerCase();
        return (
          hire.fullName?.toLowerCase().includes(searchLower) ||
          hire.email?.toLowerCase().includes(searchLower) ||
          hire.mobileNumber?.toLowerCase().includes(searchLower) ||
          hire.state?.toLowerCase().includes(searchLower) ||
          hire.instagram?.toLowerCase().includes(searchLower) ||
          hire.reference?.toLowerCase().includes(searchLower)
        );
      });
    }
    
    setFilteredHires(filtered);
  };

  const handleClearSearch = () => {
    setSearch('');
    if (selectedTeamMember) {
      const teamMemberEmail = selectedTeamMember.team_member_email || selectedTeamMember.email;
      const filtered = hires.filter(hire => {
        if (!hire.recruiter_emails) return false;
        const recruiterEmailsStr = Array.isArray(hire.recruiter_emails) 
          ? hire.recruiter_emails.join(',')
          : String(hire.recruiter_emails);
        return recruiterEmailsStr.toLowerCase().includes(teamMemberEmail.toLowerCase());
      });
      setFilteredHires(filtered);
    } else {
      setFilteredHires(hires);
    }
  };

  const handleActionsMenuOpen = (event: React.MouseEvent<HTMLElement>, hire: NewHire) => {
    setActionsMenuAnchor(event.currentTarget);
    setSelectedHireForActions(hire);
  };

  const handleActionsMenuClose = () => {
    setActionsMenuAnchor(null);
    setSelectedHireForActions(null);
  };

  const toggleStageCollapse = (stageIndex: number) => {
    setCollapsedStages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(stageIndex)) {
        newSet.delete(stageIndex);
      } else {
        newSet.add(stageIndex);
      }
      return newSet;
    });
  };

  const renderEditableCell = (hire: NewHire, field: keyof NewHire) => {
    const isEditing = editingId === hire.id;
    const value = isEditing ? editFormData[field] || '' : hire[field];

    if (isEditing && field !== 'created_at' && field !== 'update_timestamp') {
      return (
        <TextField
          fullWidth
          size="small"
          value={value}
          onChange={handleEditChange(field)}
          type="text"
        />
      );
    }

    if ((field === 'created_at' || field === 'update_timestamp') && value) {
      const date = new Date(value as string);
      return date.toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    }

    if (field === 'recruiter_emails' && Array.isArray(value)) {
      return value.join(', ');
    }

    return value || '';
  };

  // Calculate metrics
  const combinedHires = useMemo(() => [...hires], [hires]);

  const { jotformEnrolled, jotformInvitations, jotformRatio } = useMemo(() => {
    const enrolledCount = combinedHires.filter(hire => Number(hire.progress) === 2).length;
    const invitationCount = combinedHires.filter(hire => {
      const progress = Number(hire.progress);
      return progress >= 0 && progress <= 1;
    }).length;
    const ratio = invitationCount === 0 ? null : enrolledCount / invitationCount;
    return { jotformEnrolled: enrolledCount, jotformInvitations: invitationCount, jotformRatio: ratio };
  }, [combinedHires]);

  const { jotformCurrentEnrolled, jotformCurrentInvitations, jotformCurrentRatio } = useMemo(() => {
    const cutoffDate = new Date();
    cutoffDate.setMonth(cutoffDate.getMonth() - 2);
    const filtered = combinedHires.filter(hire => {
      if (!hire?.created_at) return false;
      const createdDate = new Date(hire.created_at as string);
      if (Number.isNaN(createdDate.getTime())) return false;
      return createdDate >= cutoffDate;
    });

    const enrolledCount = filtered.filter(hire => Number(hire.progress) === 2).length;
    const invitationCount = filtered.filter(hire => {
      const progress = Number(hire.progress);
      return progress >= 0 && progress <= 1;
    }).length;
    const ratio = invitationCount === 0 ? null : enrolledCount / invitationCount;

    return {
      jotformCurrentEnrolled: enrolledCount,
      jotformCurrentInvitations: invitationCount,
      jotformCurrentRatio: ratio,
    };
  }, [combinedHires]);

  const {
    hirePerCodeRecentEnrolled,
    hirePerCodeRecentWaiting,
    hirePerCodeRecentRatio,
    hirePerCodeAllTimeEnrolled,
    hirePerCodeAllTimeWaiting,
    hirePerCodeAllTimeRatio,
  } = useMemo(() => {
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - 2);

    const recentHires = combinedHires.filter(hire => {
      if (!hire?.created_at) return false;
      const createdAt = new Date(hire.created_at as string);
      if (Number.isNaN(createdAt.getTime())) return false;
      return createdAt >= cutoff;
    });

    const toProgress = (hire: NewHire) => Number(hire?.progress ?? 0);
    const isHireProgress = (progress: number) => progress >= 2 && progress <= 5;

    const recentEnrolled = recentHires.filter(hire => isHireProgress(toProgress(hire))).length;
    const recentWaiting = recentHires.filter(hire => toProgress(hire) === 6).length;
    const recentRatio = recentEnrolled === 0 ? null : recentWaiting / recentEnrolled;

    const allTimeEnrolled = combinedHires.filter(hire => isHireProgress(toProgress(hire))).length;
    const allTimeWaiting = combinedHires.filter(hire => toProgress(hire) === 6).length;
    const allTimeRatio = allTimeEnrolled === 0 ? null : allTimeWaiting / allTimeEnrolled;

    return {
      hirePerCodeRecentEnrolled: recentEnrolled,
      hirePerCodeRecentWaiting: recentWaiting,
      hirePerCodeRecentRatio: recentRatio,
      hirePerCodeAllTimeEnrolled: allTimeEnrolled,
      hirePerCodeAllTimeWaiting: allTimeWaiting,
      hirePerCodeAllTimeRatio: allTimeRatio,
    };
  }, [hires]);

  const formatNumber = (value: number | null | undefined) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
      return '0';
    }
    return Number(value).toLocaleString('en-US');
  };

  const formatPercent = (ratio: number | null) => {
    return ratio === null ? 'N/A' : `${(ratio * 100).toFixed(0)}%`;
  };

  const metricItems = useMemo(() => [
    {
      key: 'active-hires',
      label: 'Active Hires',
      value: formatNumber(stats?.activeHires ?? 0),
      helperText: 'Hires currently progressing',
      accentColor: '#d97706',
    },
    {
      key: 'jotform-all-time',
      label: 'Jotform to Hire Ratio (All Time)',
      value: formatPercent(jotformRatio),
      helperText: 'Active + archived invites',
      accentColor: '#1c7ed6',
      tooltip: `Enrolled (Progress 2): ${formatNumber(jotformEnrolled)} • Invitations (Progress 0-2): ${formatNumber(jotformInvitations)}`,
    },
    {
      key: 'jotform-recent',
      label: 'Jotform to Hire Ratio (Last 2 Months)',
      value: formatPercent(jotformCurrentRatio),
      helperText: 'Invites vs. enrollments in last 60 days',
      accentColor: '#5c7cfa',
      tooltip: `Enrolled (Progress 2): ${formatNumber(jotformCurrentEnrolled)} • Invitations (Progress 0-2): ${formatNumber(jotformCurrentInvitations)}`,
    },
    {
      key: 'hire-per-code-recent',
      label: 'Hire per code ratio (Last 2 Months)',
      value: formatPercent(hirePerCodeRecentRatio),
      helperText: 'Active hires coded in last 60 days',
      accentColor: '#0ca678',
      tooltip: `Hires (Progress 2-6): ${formatNumber(hirePerCodeRecentEnrolled)} • Coded (Progress 6): ${formatNumber(hirePerCodeRecentWaiting)}`,
    },
    {
      key: 'hire-per-code-all-time',
      label: 'All time hire per code ratio',
      value: formatPercent(hirePerCodeAllTimeRatio),
      helperText: 'Active + archived hires',
      accentColor: '#7048e8',
      tooltip: `Hires (Progress 2-6): ${formatNumber(hirePerCodeAllTimeEnrolled)} • Coded (Progress 6): ${formatNumber(hirePerCodeAllTimeWaiting)}`,
    },
  ], [
    stats,
    jotformRatio,
    jotformEnrolled,
    jotformInvitations,
    jotformCurrentRatio,
    jotformCurrentEnrolled,
    jotformCurrentInvitations,
    hirePerCodeRecentRatio,
    hirePerCodeRecentEnrolled,
    hirePerCodeRecentWaiting,
    hirePerCodeAllTimeRatio,
    hirePerCodeAllTimeEnrolled,
    hirePerCodeAllTimeWaiting,
    formatNumber,
    formatPercent,
  ]);

  const sidebarWidth = 280;
  const sidebarMinimizedWidth = 80;
  const appBarHeight = 64;
  const currentSidebarWidth = sidebarMinimized ? sidebarMinimizedWidth : sidebarWidth;

  const toggleSidebar = () => {
    setSidebarMinimized(!sidebarMinimized);
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Left Sidebar */}
      <Drawer
        variant="permanent"
        sx={{
          width: currentSidebarWidth,
          flexShrink: 0,
          transition: 'width 0.3s ease',
          '& .MuiDrawer-paper': {
            width: currentSidebarWidth,
            boxSizing: 'border-box',
            borderRight: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.12)'}`,
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(180deg, #1e1e1e 0%, #2a2a2a 100%)'
              : 'linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%)',
            top: appBarHeight,
            height: `calc(100vh - ${appBarHeight}px)`,
            transition: 'width 0.3s ease',
            overflowX: 'hidden',
          },
        }}
      >
        <Box
          sx={{
            p: sidebarMinimized ? 2 : 3,
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(180deg, #1e1e1e 0%, #2a2a2a 100%)'
              : 'linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%)',
            color: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary,
            display: 'flex',
            flexDirection: sidebarMinimized ? 'column' : 'row',
            alignItems: 'center',
            justifyContent: sidebarMinimized ? 'center' : 'space-between',
            gap: 1,
          }}
        >
          {!sidebarMinimized && (
            <Box>
              <Typography variant="h5" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
                Organization Dashboard
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {ownerProfile?.full_name || 'Demo Owner'}
              </Typography>
            </Box>
          )}
          <IconButton
            onClick={toggleSidebar}
            sx={{
              color: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary,
              background: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.08)',
              '&:hover': {
                background: theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.2)'
                  : 'rgba(0, 0, 0, 0.12)',
              },
            }}
            size="small"
          >
            {sidebarMinimized ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Box>

        <List sx={{ pt: 2 }}>
          {navigationItems.map((item) => (
            <ListItem key={item.id} disablePadding sx={{ mb: 0.5, px: sidebarMinimized ? 1 : 2 }}>
              <Tooltip title={sidebarMinimized ? item.label : ''} placement="right" arrow>
                <ListItemButton
                  onClick={() => handleTabChange(item.id)}
                  selected={selectedTab === item.id}
                  sx={{
                    borderRadius: 2,
                    py: 1.5,
                    justifyContent: sidebarMinimized ? 'center' : 'flex-start',
                    px: sidebarMinimized ? 1 : 2,
                    '&.Mui-selected': {
                      background: theme.palette.mode === 'dark'
                        ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)'
                        : 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                      color: theme.palette.mode === 'dark' ? '#90caf9' : '#667eea',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: sidebarMinimized ? 0 : 40,
                      justifyContent: 'center',
                      color: selectedTab === item.id
                        ? (theme.palette.mode === 'dark' ? '#90caf9' : '#667eea')
                        : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)'),
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {!sidebarMinimized && (
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontWeight: selectedTab === item.id ? 600 : 500,
                        fontSize: '0.95rem',
                      }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          ))}
          
          <Divider sx={{ my: 2 }} />
          
          {/* Configuration Items */}
          <ListItem disablePadding sx={{ mb: 0.5, px: sidebarMinimized ? 1 : 2 }}>
            <Tooltip title={sidebarMinimized ? 'Checklist Content Settings' : ''} placement="right" arrow>
              <ListItemButton
                onClick={() => {
                  setConfigTab(configTab === 'content' ? null : 'content');
                  setSelectedTab(0);
                }}
                selected={configTab === 'content'}
                sx={{
                  borderRadius: 2,
                  py: 1.5,
                  justifyContent: sidebarMinimized ? 'center' : 'flex-start',
                  px: sidebarMinimized ? 1 : 2,
                  '&.Mui-selected': {
                    background: theme.palette.mode === 'dark'
                      ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)'
                      : 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                    color: theme.palette.mode === 'dark' ? '#90caf9' : '#667eea',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: sidebarMinimized ? 0 : 40,
                    justifyContent: 'center',
                    color: configTab === 'content'
                      ? (theme.palette.mode === 'dark' ? '#90caf9' : '#667eea')
                      : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)'),
                  }}
                >
                  <SettingsIcon />
                </ListItemIcon>
                {!sidebarMinimized && (
                  <ListItemText
                    primary="Checklist Content"
                    primaryTypographyProps={{
                      fontWeight: configTab === 'content' ? 600 : 500,
                      fontSize: '0.95rem',
                    }}
                  />
                )}
              </ListItemButton>
            </Tooltip>
          </ListItem>

          <ListItem disablePadding sx={{ mb: 0.5, px: sidebarMinimized ? 1 : 2 }}>
            <Tooltip title={sidebarMinimized ? 'Checklist Automation Settings' : ''} placement="right" arrow>
              <ListItemButton
                onClick={() => {
                  setConfigTab(configTab === 'automation' ? null : 'automation');
                  setSelectedTab(0);
                }}
                selected={configTab === 'automation'}
                sx={{
                  borderRadius: 2,
                  py: 1.5,
                  justifyContent: sidebarMinimized ? 'center' : 'flex-start',
                  px: sidebarMinimized ? 1 : 2,
                  '&.Mui-selected': {
                    background: theme.palette.mode === 'dark'
                      ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)'
                      : 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                    color: theme.palette.mode === 'dark' ? '#90caf9' : '#667eea',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: sidebarMinimized ? 0 : 40,
                    justifyContent: 'center',
                    color: configTab === 'automation'
                      ? (theme.palette.mode === 'dark' ? '#90caf9' : '#667eea')
                      : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)'),
                  }}
                >
                  <SettingsIcon />
                </ListItemIcon>
                {!sidebarMinimized && (
                  <ListItemText
                    primary="Checklist Automation"
                    primaryTypographyProps={{
                      fontWeight: configTab === 'automation' ? 600 : 500,
                      fontSize: '0.95rem',
                    }}
                  />
                )}
              </ListItemButton>
            </Tooltip>
          </ListItem>

          <ListItem disablePadding sx={{ mb: 0.5, px: sidebarMinimized ? 1 : 2 }}>
            <Tooltip title={sidebarMinimized ? 'Progress Stage Settings' : ''} placement="right" arrow>
              <ListItemButton
                onClick={() => {
                  setConfigTab(configTab === 'progressStages' ? null : 'progressStages');
                  setSelectedTab(0);
                }}
                selected={configTab === 'progressStages'}
                sx={{
                  borderRadius: 2,
                  py: 1.5,
                  justifyContent: sidebarMinimized ? 'center' : 'flex-start',
                  px: sidebarMinimized ? 1 : 2,
                  '&.Mui-selected': {
                    background: theme.palette.mode === 'dark'
                      ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)'
                      : 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                    color: theme.palette.mode === 'dark' ? '#90caf9' : '#667eea',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: sidebarMinimized ? 0 : 40,
                    justifyContent: 'center',
                    color: configTab === 'progressStages'
                      ? (theme.palette.mode === 'dark' ? '#90caf9' : '#667eea')
                      : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)'),
                  }}
                >
                  <SettingsIcon />
                </ListItemIcon>
                {!sidebarMinimized && (
                  <ListItemText
                    primary="Progress Stages"
                    primaryTypographyProps={{
                      fontWeight: configTab === 'progressStages' ? 600 : 500,
                      fontSize: '0.95rem',
                    }}
                  />
                )}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        </List>
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: `calc(100% - ${currentSidebarWidth}px)`,
          transition: 'width 0.3s ease',
        }}
      >
        {selectedTab === 0 && (
          <>
            {configTab === 'content' ? (
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    Checklist Content Settings
                  </Typography>
                  <Button onClick={() => setConfigTab(null)} variant="outlined">
                    Back to Overview
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {demoChecklistContent.map((step, index) => (
                    <Accordion key={index} elevation={2}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                          <DragIndicatorIcon sx={{ color: 'text.secondary' }} />
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Step {index + 1}: {step.title}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {step.content.map((item, itemIndex) => (
                            <Box key={itemIndex} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                              <CheckCircleIcon color="success" fontSize="small" />
                              <Typography variant="body1">{item.text}</Typography>
                            </Box>
                          ))}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              </Paper>
            ) : configTab === 'automation' ? (
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    Checklist Automation Settings
                  </Typography>
                  <Button onClick={() => setConfigTab(null)} variant="outlined">
                    Back to Overview
                  </Button>
                </Box>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Checklist Number</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Progress Stage</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[
                        { checklist: '1', progress: 'Invitation Sent' },
                        { checklist: '2', progress: 'Enrolled in Course' },
                        { checklist: '3', progress: 'Passed the Test' },
                        { checklist: '4', progress: 'Company Onboarding' },
                      ].map((rule, index) => (
                        <TableRow key={index} hover>
                          <TableCell>
                            <Chip label={rule.checklist} color="primary" size="small" />
                          </TableCell>
                          <TableCell>{rule.progress}</TableCell>
                          <TableCell>
                            <IconButton size="small">
                              <EditIcon />
                            </IconButton>
                            <IconButton size="small">
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Box sx={{ mt: 2 }}>
                  <Button startIcon={<AddIcon />} variant="contained">
                    Add Automation Rule
                  </Button>
                </Box>
              </Paper>
            ) : configTab === 'progressStages' ? (
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    Progress Stage Settings
                  </Typography>
                  <Button onClick={() => setConfigTab(null)} variant="outlined">
                    Back to Overview
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {progressStages.map((stage, index) => (
                    <Card key={index} elevation={2}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <DragIndicatorIcon sx={{ color: 'text.secondary' }} />
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                              <Chip
                                label={`Stage ${stage.value}`}
                                sx={{ backgroundColor: stage.color, color: 'white', fontWeight: 600 }}
                              />
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {stage.label}
                              </Typography>
                            </Box>
                            <Typography variant="body2" color="textSecondary">
                              {stage.description}
                            </Typography>
                          </Box>
                          <IconButton>
                            <EditIcon />
                          </IconButton>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
                <Box sx={{ mt: 2 }}>
                  <Button startIcon={<AddIcon />} variant="contained">
                    Add New Stage
                  </Button>
                </Box>
              </Paper>
            ) : (
              <>
            {/* Metrics Cards */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 3, mb: 4 }}>
              {metricItems.map((item) => (
                <Paper
                  key={item.key}
                  elevation={3}
                  sx={{
                    p: 3,
                    background: theme.palette.mode === 'dark'
                      ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.1) 100%)'
                      : 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(102, 126, 234, 0.2)'}`,
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '4px',
                      background: `linear-gradient(90deg, ${item.accentColor} 0%, ${alpha(item.accentColor, 0.5)} 100%)`,
                    },
                  }}
                >
                  <Tooltip title={item.tooltip || ''} arrow>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: item.accentColor }}>
                        {item.value}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {item.label}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {item.helperText}
                      </Typography>
                    </Box>
                  </Tooltip>
                </Paper>
              ))}
            </Box>

            {/* Search and Filters */}
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                placeholder="Search hires by name, email, phone, state, Instagram, or reference..."
                value={search}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: search && (
                    <InputAdornment position="end">
                      <IconButton onClick={handleClearSearch} size="small">
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />

              {/* Team Member Buttons */}
              <Box sx={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: 2, 
                mb: 3,
                p: 2,
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.06) 100%)'
                  : 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
                borderRadius: 2,
                border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(102, 126, 234, 0.1)'}`,
              }}>
                <Typography variant="h6" sx={{ width: '100%', mb: 2, fontWeight: 600 }}>
                  Filters:
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleShowAllHires}
                  sx={{
                    background: selectedTeamMember 
                      ? 'transparent'
                      : (theme.palette.mode === 'dark'
                          ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'),
                    color: selectedTeamMember 
                      ? (theme.palette.mode === 'dark' ? '#667eea' : '#667eea')
                      : '#ffffff',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '8px',
                    px: 3,
                    py: 1,
                    border: selectedTeamMember 
                      ? `2px solid ${theme.palette.mode === 'dark' ? '#667eea' : '#667eea'}`
                      : 'none',
                  }}
                >
                  Show All Hires
                </Button>
                
                {ownerTeamMembers.length > 0 && (
                  <>
                    <Typography variant="subtitle1" sx={{ width: '100%', mt: 2, mb: 1, fontWeight: 500 }}>
                      Org Owner(s):
                    </Typography>
                    {ownerTeamMembers.map((member) => (
                      <Button
                        key={member.id}
                        variant="contained"
                        onClick={() => handleOwnerMemberClick(member)}
                        sx={{
                          background: selectedTeamMember?.id === member.id
                            ? 'transparent'
                            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: selectedTeamMember?.id === member.id
                            ? (theme.palette.mode === 'dark' ? '#667eea' : '#667eea')
                            : '#ffffff',
                          fontWeight: 600,
                          textTransform: 'none',
                          borderRadius: '8px',
                          px: 3,
                          py: 1,
                          border: selectedTeamMember?.id === member.id
                            ? `2px solid ${theme.palette.mode === 'dark' ? '#667eea' : '#667eea'}`
                            : 'none',
                        }}
                      >
                        {member.full_name}
                      </Button>
                    ))}
                  </>
                )}
                
                {adminTeamMembers.length > 0 && (
                  <>
                    <Typography variant="subtitle1" sx={{ width: '100%', mt: 2, mb: 1, fontWeight: 500 }}>
                      Admins:
                    </Typography>
                    {adminTeamMembers.map((member) => (
                      <Button
                        key={member.id}
                        variant="contained"
                        onClick={() => handleTeamMemberClick(member)}
                        sx={{
                          background: selectedTeamMember?.id === member.id
                            ? 'transparent'
                            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: selectedTeamMember?.id === member.id
                            ? (theme.palette.mode === 'dark' ? '#667eea' : '#667eea')
                            : '#ffffff',
                          fontWeight: 600,
                          textTransform: 'none',
                          borderRadius: '8px',
                          px: 3,
                          py: 1,
                          border: selectedTeamMember?.id === member.id
                            ? `2px solid ${theme.palette.mode === 'dark' ? '#667eea' : '#667eea'}`
                            : 'none',
                        }}
                      >
                        {member.full_name}
                      </Button>
                    ))}
                  </>
                )}
              </Box>
            </Box>

            {/* Progress Stages Tables */}
            {progressStages.map((stage, stageIndex) => {
              const stageHires = filteredHires.filter(hire => hire.progress === stage.value);
              let stageTableColumns = getTableColumnsForStage(stage.value, agentNumberCheckboxStage);
              if (stage.value === 0) {
                stageTableColumns = stageTableColumns.filter(col => col !== 'Updated At');
              }
              
              const isCollapsed = collapsedStages.has(stageIndex);
              
              return (
                <Box key={stageIndex} sx={{ mb: 4 }}>
                  <Box 
                    onClick={() => toggleStageCollapse(stageIndex)}
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      p: 2,
                      background: theme.palette.mode === 'dark'
                        ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.12) 100%)'
                        : 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(102, 126, 234, 0.3)'}`,
                      mb: 2,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Chip
                        label={stage.label}
                        sx={{
                          backgroundColor: stage.color || '#667eea',
                          color: '#ffffff',
                          fontWeight: 600,
                          fontSize: '0.9rem',
                          px: 1,
                        }}
                      />
                      <Typography variant="body2" color="textSecondary">
                        {stageHires.length} {stageHires.length === 1 ? 'hire' : 'hires'}
                      </Typography>
                    </Box>
                    <IconButton size="small">
                      {isCollapsed ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
                    </IconButton>
                  </Box>
                  
                  {!isCollapsed && (
                    <TableContainer 
                      component={Paper} 
                      sx={{ 
                        mb: 2,
                        borderRadius: 3,
                        overflow: 'hidden',
                        maxHeight: '600px',
                        overflowX: 'auto',
                        overflowY: 'auto',
                      }}
                    >
                      <Table sx={{ minWidth: { xs: stage.value === agentNumberCheckboxStage ? 950 : 900, md: 'auto' } }}>
                        <TableHead>
                          <TableRow>
                            {stageTableColumns.map((column, index) => (
                              <TableCell 
                                key={index}
                                sx={{
                                  position: 'sticky',
                                  top: 0,
                                  zIndex: 15,
                                  backgroundColor: theme.palette.mode === 'dark' 
                                    ? 'rgba(42, 42, 42, 0.95)'
                                    : 'rgba(255, 255, 255, 0.95)',
                                  color: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary,
                                  fontWeight: 600,
                                  fontSize: '0.9rem',
                                  textAlign: 'center',
                                  py: 2,
                                  textTransform: 'uppercase',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {column}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {stageHires.map((hire) => (
                            <TableRow 
                              key={hire.id}
                              sx={{
                                '&:nth-of-type(odd)': {
                                  backgroundColor: theme.palette.mode === 'dark'
                                    ? 'rgba(255, 255, 255, 0.02)'
                                    : 'rgba(102, 126, 234, 0.03)',
                                },
                                '&:hover': {
                                  backgroundColor: theme.palette.mode === 'dark'
                                    ? 'rgba(255, 255, 255, 0.08)'
                                    : 'rgba(102, 126, 234, 0.08)',
                                },
                              }}
                            >
                              <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'center' }}>
                                <Tooltip title="Actions">
                                  <IconButton 
                                    onClick={(event) => handleActionsMenuOpen(event, hire)} 
                                    size="small"
                                  >
                                    <MoreVertIcon />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'center' }}>
                                <Tooltip title="Notes">
                                  <IconButton 
                                    onClick={() => {
                                      setSelectedHireForNotes(hire);
                                      setNotesDialogOpen(true);
                                    }}
                                    size="small"
                                  >
                                    <NoteIcon />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                              {agentNumberCheckboxStage !== null && stage.value === agentNumberCheckboxStage && (
                                <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'center' }}>
                                  <Checkbox
                                    checked={hire.agent_appointment_sent || false}
                                    disabled
                                  />
                                </TableCell>
                              )}
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{renderEditableCell(hire, 'created_at')}</TableCell>
                              {stage.value !== 0 && (
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{renderEditableCell(hire, 'update_timestamp')}</TableCell>
                              )}
                              <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 120 }}>
                                <TimeSinceCell
                                  dateString={stage.value === 0 
                                    ? (hire.created_at as string)
                                    : (hire.update_timestamp as string)
                                  }
                                  label={stage.value === 0 ? 'created' : 'updated'}
                                  displayDate={stage.value === 0 
                                    ? renderEditableCell(hire, 'created_at') as string
                                    : renderEditableCell(hire, 'update_timestamp') as string
                                  }
                                />
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 150 }}>{renderEditableCell(hire, 'fullName')}</TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{renderEditableCell(hire, 'state')}</TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap', minWidth: 200 }}>{renderEditableCell(hire, 'email')}</TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{renderEditableCell(hire, 'mobileNumber')}</TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{renderEditableCell(hire, 'instagram')}</TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{renderEditableCell(hire, 'reference')}</TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{renderEditableCell(hire, 'recruiter_numbers')}</TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{renderEditableCell(hire, 'recruiter_emails')}</TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'center' }}>
                                {editingId === hire.id ? (
                                  <>
                                    <Tooltip title="Save">
                                      <IconButton onClick={handleSave} size="small" sx={{ color: '#4caf50' }}>
                                        <SaveIcon />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Cancel">
                                      <IconButton onClick={handleCancel} size="small" sx={{ color: '#f44336' }}>
                                        <CancelIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </>
                                ) : (
                                  <Tooltip title="Edit">
                                    <IconButton onClick={() => handleEdit(hire)} size="small">
                                      <EditIcon />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              );
            })}
              </>
            )}
          </>
        )}

        {selectedTab === 1 && (
          <TableContainer component={Paper} elevation={2}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Team Member Email</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Hire per Code Ratio (Last 2 Months)</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Hire per Code Ratio (All Time)</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Jotform to Hire Ratio (Last 2 Months)</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Jotform to Hire Ratio (All Time)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {teamMembers.map((member) => {
                  const metric = teamMemberMetrics[member.id];
                  
                  return (
                    <TableRow key={member.id} hover>
                      <TableCell>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {member.full_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={member.role}
                          color={getRoleColor(member.role) as any}
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {member.team_member_email || member.email || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={`Enrolled in Course: ${metric.currentEnrolled} • Waiting for Training: ${metric.currentWaiting}`}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {metric.currentRatio === null ? 'N/A' : `${(metric.currentRatio * 100).toFixed(1)}%`}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={`Enrolled in Course: ${metric.enrolled} • Waiting for Training: ${metric.waiting}`}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {metric.ratio === null ? 'N/A' : `${(metric.ratio * 100).toFixed(1)}%`}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={`Jotform Enrolled (Progress 2): ${metric.jotformCurrentEnrolled} • Jotform Invitations (Progress 0-2): ${metric.jotformCurrentInvitations}`}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {metric.jotformCurrentRatio === null ? 'N/A' : `${(metric.jotformCurrentRatio * 100).toFixed(1)}%`}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={`Jotform Enrolled (Progress 2): ${metric.jotformEnrolled} • Jotform Invitations (Progress 0-2): ${metric.jotformInvitations}`}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {metric.jotformRatio === null ? 'N/A' : `${(metric.jotformRatio * 100).toFixed(1)}%`}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {selectedTab === 2 && (
          <Box>
            {/* Analytics Header */}
            <Paper
              elevation={3}
              sx={{
                p: 3,
                mb: 3,
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, #2a2a2a 0%, #3a3a3a 100%)'
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
              }}
            >
              <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
                Analytics Dashboard
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9 }}>
                Organization-wide Analytics
              </Typography>
            </Paper>

            {/* Summary Cards */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
              <Card elevation={3} sx={{ flex: '1 1 250px', minWidth: '250px' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="h6">
                        Total Hires
                      </Typography>
                      <Typography variant="h4" component="div" sx={{ fontWeight: 700 }}>
                        {hires.length}
                      </Typography>
                    </Box>
                    <PeopleIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
                  </Box>
                </CardContent>
              </Card>

              <Card elevation={3} sx={{ flex: '1 1 250px', minWidth: '250px' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="h6">
                        In Progress
                      </Typography>
                      <Typography variant="h4" component="div" sx={{ fontWeight: 700 }}>
                        {hires.filter(h => h.progress < 6).length}
                      </Typography>
                    </Box>
                    <ScheduleIcon sx={{ fontSize: 40, color: theme.palette.warning.main }} />
                  </Box>
                </CardContent>
              </Card>

              <Card elevation={3} sx={{ flex: '1 1 250px', minWidth: '250px' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="h6">
                        Completed
                      </Typography>
                      <Typography variant="h4" component="div" sx={{ fontWeight: 700 }}>
                        {hires.filter(h => h.progress === 6).length}
                      </Typography>
                    </Box>
                    <CheckCircleIcon sx={{ fontSize: 40, color: theme.palette.success.main }} />
                  </Box>
                </CardContent>
              </Card>

              <Card elevation={3} sx={{ flex: '1 1 250px', minWidth: '250px' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="h6">
                        Completion Rate
                      </Typography>
                      <Typography variant="h4" component="div" sx={{ fontWeight: 700 }}>
                        {hires.length > 0 ? Math.round((hires.filter(h => h.progress === 6).length / hires.length) * 100) : 0}%
                      </Typography>
                    </Box>
                    <TrendingUpIcon sx={{ fontSize: 40, color: theme.palette.info.main }} />
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {/* Progress Chart */}
            <Paper elevation={3} sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                Hire Progress Through Stages
              </Typography>
              
              <Box sx={{ mb: 4 }}>
                {progressStages.map((stage, index) => {
                  const stageHires = hires.filter(h => h.progress === stage.value);
                  const count = stageHires.length;
                  const percentage = hires.length > 0 ? (count / hires.length) * 100 : 0;
                  
                  return (
                    <Box key={index} sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 500, flex: 1 }}>
                          {stage.label}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Typography variant="body2" color="textSecondary">
                            {count} hires
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            ({percentage.toFixed(1)}%)
                          </Typography>
                        </Box>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={percentage}
                        sx={{
                          height: 12,
                          borderRadius: 6,
                          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 6,
                            background: stage.color,
                            boxShadow: `0 0 10px ${stage.color}40`,
                          },
                        }}
                      />
                    </Box>
                  );
                })}
              </Box>

              {/* Stage Distribution Summary */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {progressStages.map((stage, index) => {
                  const count = hires.filter(h => h.progress === stage.value).length;
                  return (
                    <Card
                      key={index}
                      elevation={2}
                      sx={{
                        p: 2,
                        textAlign: 'center',
                        flex: '1 1 200px',
                        minWidth: '200px',
                        background: theme.palette.mode === 'dark'
                          ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.1) 100%)'
                          : 'linear-gradient(135deg, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 1) 100%)',
                        border: `2px solid ${stage.color}20`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: `0 4px 20px ${stage.color}30`,
                        },
                      }}
                    >
                      <Typography variant="h4" sx={{ fontWeight: 700, color: stage.color }}>
                        {count}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.8rem' }}>
                        {stage.label}
                      </Typography>
                    </Card>
                  );
                })}
              </Box>
            </Paper>
          </Box>
        )}

        {selectedTab === 3 && (
          <Box>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                mb: 3,
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, #2a2a2a 0%, #3a3a3a 100%)'
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
              }}
            >
              <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
                SMS Manager
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9 }}>
                Manage SMS communications and automated messages
              </Typography>
            </Paper>

            <Paper elevation={2} sx={{ mb: 3 }}>
              <Tabs value={smsTab} onChange={(e, v) => setSmsTab(v)}>
                <Tab label="Frontend SMS" />
                <Tab label="Backend SMS" />
                <Tab label="Automated AI Messages" />
                <Tab label="Email Templates" />
              </Tabs>
            </Paper>

            {smsTab === 0 && (
              <Box>
                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                  <Paper elevation={2} sx={{ p: 2, flex: 1 }}>
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                      Total Messages
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {demoSMS.length}
                    </Typography>
                  </Paper>
                  <Paper elevation={2} sx={{ p: 2, flex: 1 }}>
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                      Unread Messages
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {demoSMS.filter(m => !m.seen && m.direction === 'inbound').length}
                    </Typography>
                  </Paper>
                  <Paper elevation={2} sx={{ p: 2, flex: 1 }}>
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                      Outbound Messages
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {demoSMS.filter(m => m.direction === 'outbound').length}
                    </Typography>
                  </Paper>
                </Box>

                <TableContainer component={Paper} elevation={2}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Direction</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Message</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {demoSMS.map((sms) => (
                        <TableRow key={sms.id} hover>
                          <TableCell>{sms.name}</TableCell>
                          <TableCell>{sms.number}</TableCell>
                          <TableCell>
                            <Chip
                              label={sms.direction}
                              color={sms.direction === 'inbound' ? 'primary' : 'secondary'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell sx={{ maxWidth: 300 }}>
                            <Typography variant="body2" noWrap>
                              {sms.message}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {new Date(sms.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            {sms.direction === 'inbound' && !sms.seen ? (
                              <Badge badgeContent="New" color="error">
                                <SmsIcon />
                              </Badge>
                            ) : (
                              <CheckCircleIcon color="success" />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {smsTab === 1 && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Backend SMS Logs
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Timestamp</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Recipient</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Message</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Cost</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {demoSMS.filter(m => m.direction === 'outbound').map((sms) => (
                        <TableRow key={sms.id} hover>
                          <TableCell>{new Date(sms.created_at).toLocaleString()}</TableCell>
                          <TableCell>{sms.number}</TableCell>
                          <TableCell sx={{ maxWidth: 300 }}>
                            <Typography variant="body2" noWrap>
                              {sms.message}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label="Delivered" color="success" size="small" />
                          </TableCell>
                          <TableCell>${sms.cost}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            {smsTab === 2 && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                  Automated AI Text Messages
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {demoAIMessages.map((ai) => (
                    <Card key={ai.id} elevation={2}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {ai.trigger}
                            </Typography>
                            <Chip
                              label={ai.enabled ? 'Enabled' : 'Disabled'}
                              color={ai.enabled ? 'success' : 'default'}
                              size="small"
                              sx={{ mt: 1 }}
                            />
                          </Box>
                        </Box>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                            borderRadius: 1,
                          }}
                        >
                          <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                            "{ai.message}"
                          </Typography>
                        </Paper>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </Paper>
            )}

            {smsTab === 3 && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                  Email Templates
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {[
                    { id: '1', name: 'Welcome Email', subject: 'Welcome to Our Team' },
                    { id: '2', name: 'Enrollment Reminder', subject: 'Complete Your Enrollment' },
                    { id: '3', name: 'Training Notification', subject: 'Training Course Available' },
                    { id: '4', name: 'License Approval', subject: 'License Approved' },
                  ].map((template) => (
                    <Card key={template.id} elevation={2}>
                      <CardContent>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                          {template.name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Subject: {template.subject}
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </Paper>
            )}
          </Box>
        )}

        {selectedTab === 4 && (
          <Box>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                mb: 3,
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, #2a2a2a 0%, #3a3a3a 100%)'
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
                    Email Reminders
                  </Typography>
                  <Typography variant="h6" sx={{ opacity: 0.9 }}>
                    View all reminder emails sent from your organization
                  </Typography>
                </Box>
                <Tooltip title="Refresh">
                  <IconButton
                    sx={{
                      color: 'white',
                      background: 'rgba(255, 255, 255, 0.1)',
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.2)',
                      },
                    }}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Paper>

            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <Paper elevation={2} sx={{ p: 2, flex: 1 }}>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  Total Emails
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {demoEmails.length}
                </Typography>
              </Paper>
            </Box>

            <TableContainer component={Paper} elevation={3}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>From</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>To</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Subject</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {demoEmails.map((email) => (
                    <TableRow key={email.id} hover>
                      <TableCell>
                        {new Date(email.sent_at).toLocaleString()}
                      </TableCell>
                      <TableCell>{email.from}</TableCell>
                      <TableCell>{email.to}</TableCell>
                      <TableCell>{email.subject}</TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedEmail(email);
                            setEmailDialogOpen(true);
                          }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Dialog
              open={emailDialogOpen}
              onClose={() => setEmailDialogOpen(false)}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle>
                {selectedEmail?.subject}
              </DialogTitle>
              <DialogContent>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    <strong>From:</strong> {selectedEmail?.from}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    <strong>To:</strong> {selectedEmail?.to}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Sent:</strong> {selectedEmail ? new Date(selectedEmail.sent_at).toLocaleString() : ''}
                  </Typography>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {selectedEmail?.body?.replace(/<[^>]*>/g, '')}
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setEmailDialogOpen(false)}>Close</Button>
              </DialogActions>
            </Dialog>
          </Box>
        )}

        {/* Actions Menu */}
        <Menu
          anchorEl={actionsMenuAnchor}
          open={Boolean(actionsMenuAnchor)}
          onClose={handleActionsMenuClose}
        >
          <MenuItem onClick={handleActionsMenuClose}>
            <AssignmentIcon sx={{ mr: 1 }} />
            Assign Recruiter
          </MenuItem>
          <MenuItem onClick={handleActionsMenuClose}>
            <ArchiveIcon sx={{ mr: 1 }} />
            Archive
          </MenuItem>
        </Menu>

        {/* Notes Dialog */}
        <Dialog
          open={notesDialogOpen}
          onClose={() => setNotesDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Notes</DialogTitle>
          <DialogContent>
            <Typography variant="body1">
              {selectedHireForNotes?.notes || 'No notes available'}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setNotesDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        <TrademarkFooter />
      </Box>
    </Box>
  );
};

export default OwnerDashboardDemo;
